import React, { useMemo, useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./CustomCalendar.css";

/** Helpers */
const addMonths = (date, delta) => {
  const d = new Date(date);
  d.setMonth(d.getMonth() + delta);
  return d;
};

const startOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1);
const endOfMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0);
const isSameDay = (a, b) =>
  a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const isBefore = (a, b) => a.getTime() < b.getTime();

const clampDate = (d, min, max) => {
  if (min && d < min) return min;
  if (max && d > max) return max;
  return d;
};

const formatMonthYear = (date) =>
  date.toLocaleDateString(undefined, { month: "long", year: "numeric" });

const daysInMonth = (date) => endOfMonth(date).getDate();

/** Build a 6x7 calendar grid, Sunday as first day */
function buildCalendarMatrix(viewDate) {
  const start = startOfMonth(viewDate);

  const firstDayIndex = start.getDay();

  const prevDaysCount = firstDayIndex;

  const result = [];
  const cursor = new Date(start);
  cursor.setDate(1 - prevDaysCount); // move backward to fill the first week

  for (let week = 0; week < 6; week++) {
    const row = [];
    for (let day = 0; day < 7; day++) {
      const cellDate = new Date(cursor);
      row.push({
        date: cellDate,
        inCurrentMonth: cellDate.getMonth() === viewDate.getMonth(),
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    result.push(row);
  }
  return result;
}

function normalizeRange(a, b) {
  if (!a && !b) return { start: null, end: null };
  if (a && !b) return { start: a, end: null };
  if (!a && b) return { start: b, end: null };
  return isBefore(a, b) ? { start: a, end: b } : { start: b, end: a };
}

/** Props
 * mode: "single" | "range" (default: "range")
 * value: Date (single) OR {start?: Date, end?: Date} (range)
 * onChange: (value) => void
 * onDone?: (value) => void
 * onCancel?: () => void
 * minDate?: Date
 * maxDate?: Date
 * initialMonth?: Date
 * showSlider?: boolean (default: true)
 */
export default function CustomCalendar({
  mode = "range",
  value,
  onChange,
  onDone,
  onCancel,
  minDate,
  maxDate,
  initialMonth,
  showSlider = true,
}) {
  const today = useMemo(() => new Date(), []);
  const initialView = useMemo(() => {
    if (initialMonth) return startOfMonth(initialMonth);
    if (mode === "single" && value instanceof Date) return startOfMonth(value);
    if (mode === "range" && value && value.start instanceof Date)
      return startOfMonth(value.start);
    return startOfMonth(today);
  }, [initialMonth, mode, value, today]);

  const [viewDate, setViewDate] = useState(initialView);

  // Internal selection state
  const [single, setSingle] = useState(
    mode === "single" && value instanceof Date ? value : null
  );
  const [range, setRange] = useState(() => {
    if (mode === "range" && value && (value.start || value.end)) {
      const { start, end } = value;
      return { start: start || null, end: end || null };
    }
    return { start: null, end: null };
  });

  const [isDraggingRange, setIsDraggingRange] = useState(false);
  const [hoverDate, setHoverDate] = useState(null);

  // Slider (days within the currently viewed month)
  const dim = daysInMonth(viewDate);
  const [sliderStart, setSliderStart] = useState(1);
  const [sliderEnd, setSliderEnd] = useState(dim);

  // Sync slider with current in-month selection
  useEffect(() => {
    if (mode !== "range") return;
    const inMonth = (d) => d?.getMonth() === viewDate.getMonth() && d?.getFullYear() === viewDate.getFullYear();

    if (range?.start && range?.end && inMonth(range.start) && inMonth(range.end)) {
      setSliderStart(range.start.getDate());
      setSliderEnd(range.end.getDate());
    } else if (!range.start && !range.end) {
      setSliderStart(1);
      setSliderEnd(dim);
    }
  }, [range, viewDate, dim, mode]);

  const matrix = useMemo(() => buildCalendarMatrix(viewDate), [viewDate]);

  // Derived display helpers
  const isInSelectedRange = (d) => {
    if (mode === "single") return single && isSameDay(single, d);
    const { start, end } = normalizeRange(range.start, range.end ?? hoverDate ?? range.end);
    if (!start || !end) return false;
    return d >= start && d <= end;
  };

  const isRangeStart = (d) => {
    if (mode !== "range") return false;
    const { start, end } = normalizeRange(range.start, range.end ?? hoverDate ?? range.end);
    return !!(start && isSameDay(d, start) && end);
  };
  const isRangeEnd = (d) => {
    if (mode !== "range") return false;
    const { start, end } = normalizeRange(range.start, range.end ?? hoverDate ?? range.end);
    return !!(end && isSameDay(d, end) && start);
  };

  const handleDayClick = (d) => {
    const clamped = clampDate(d, minDate, maxDate);

    if (mode === "single") {
      setSingle(clamped);
      onChange?.(clamped);
      return;
    }

    // Range selection behavior
    if (!range.start || (range.start && range.end)) {
      // start a new range
      setRange({ start: clamped, end: null });
      setIsDraggingRange(true);
    } else if (range.start && !range.end) {
      // close the range
      const { start, end } = normalizeRange(range.start, clamped);
      setRange({ start, end });
      onChange?.({ start, end });
      setIsDraggingRange(false);
    }
  };

  const handleDayMouseEnter = (d) => {
    if (mode === "range" && isDraggingRange && range.start && !range.end) {
      setHoverDate(d);
    }
  };

  const handleMouseLeaveGrid = () => {
    if (mode === "range" && isDraggingRange) {
      setHoverDate(null);
    }
  };

  const onPrevMonth = () => setViewDate((d) => addMonths(d, -1));
  const onNextMonth = () => setViewDate((d) => addMonths(d, +1));

  const onSliderChange = (which, val) => {
    const n = Number(val);
    if (which === "start") {
      setSliderStart(Math.min(n, sliderEnd));
      if (mode === "range") {
        const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), Math.min(n, sliderEnd));
        const end = new Date(viewDate.getFullYear(), viewDate.getMonth(), sliderEnd);
        setRange({ start, end });
        onChange?.({ start, end });
      }
    } else {
      setSliderEnd(Math.max(n, sliderStart));
      if (mode === "range") {
        const start = new Date(viewDate.getFullYear(), viewDate.getMonth(), sliderStart);
        const end = new Date(viewDate.getFullYear(), viewDate.getMonth(), Math.max(n, sliderStart));
        setRange({ start, end });
        onChange?.({ start, end });
      }
    }
  };

  const onCancelClick = () => {
    onCancel?.();
  };

  const onDoneClick = () => {
    if (mode === "single") {
      onDone?.(single || null);
    } else {
      const { start, end } = normalizeRange(range.start, range.end);
      onDone?.(start && end ? { start, end } : null);
    }
  };

  // Compute slider background range for track
  const sliderMin = 1;
  const sliderMax = dim;
  const progressLeft = ((Math.min(sliderStart, sliderEnd) - sliderMin) / (sliderMax - sliderMin)) * 100;
  const progressRight = ((Math.max(sliderStart, sliderEnd) - sliderMin) / (sliderMax - sliderMin)) * 100;

  return (
    <div className="cal-card">
      <div className="cal-header">
        <button className="nav-btn" onClick={onPrevMonth} aria-label="Previous month">
          {/* left arrow */}
          <svg width="18" height="18" viewBox="0 0 24 24" className="icon">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
        <div className="cal-title">{formatMonthYear(viewDate)}</div>
        <button className="nav-btn" onClick={onNextMonth} aria-label="Next month">
          {/* right arrow */}
          <svg width="18" height="18" viewBox="0 0 24 24" className="icon">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>

      <div className="cal-dow">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="dow">{d}</div>
        ))}
      </div>
       <table
        className="cal-grid"
        onMouseLeave={handleMouseLeaveGrid}
      >
        <tbody>
          {matrix.map((week) => (
            <tr key={`week-${week[0]?.date?.toISOString?.() || "na"}`} className="cal-row">
              {week.map((cell) => {
                const { date: d, inCurrentMonth } = cell;
                const disabled = (minDate && d < startOfDay(minDate)) || (maxDate && d > endOfDay(maxDate));
                const selectedSingle = mode === "single" && single && isSameDay(single, d);
                const inRange = mode === "range" && isInSelectedRange(d);
                const start = isRangeStart(d);
                const end = isRangeEnd(d);
                const classes = [
                  "cal-cell",
                  inCurrentMonth ? "" : "muted",
                  disabled ? "disabled" : "",
                  selectedSingle ? "selected-single" : "",
                  inRange ? "in-range" : "",
                  start ? "range-start" : "",
                  end ? "range-end" : "",
                  isSameDay(d, today) ? "today" : "",
                ]
                  .filter(Boolean)
                  .join(" ");
                return (
                  <td key={d.toISOString()}>
                    <button
                      type="button"
                      className={classes}
                      onClick={() => !disabled && handleDayClick(d)}
                      onMouseEnter={() => !disabled && handleDayMouseEnter(d)}
                      disabled={disabled}
                      aria-label={d.toDateString()}
                    >
                      <span className="date-num">{d.getDate()}</span>
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {showSlider && mode === "range" && (
        <div className="cal-slider">
          <div className="slider-title">Slide to choose a monthly range.</div>

          <div
            className="slider-wrap"
            style={{
              // CSS vars to paint the selected range on the track background
              "--range-left": `${progressLeft}%`,
              "--range-right": `${progressRight}%`,
            }}
          >
            {/* Two thumbs over the same track */}
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              value={sliderStart}
              onChange={(e) => onSliderChange("start", e.target.value)}
              className="range-input range-start"
              aria-label="Range start day"
            />
            <input
              type="range"
              min={sliderMin}
              max={sliderMax}
              value={sliderEnd}
              onChange={(e) => onSliderChange("end", e.target.value)}
              className="range-input range-end"
              aria-label="Range end day"
            />
          </div>

          <div className="slider-footer">
            <span>
              {new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })}
            </span>
            <span>
              {new Date(viewDate.getFullYear(), viewDate.getMonth(), dim).toLocaleDateString(undefined, {
                day: "numeric",
                month: "short",
              })}
            </span>
          </div>
        </div>
      )}

      <div className="cal-actions">
        <button className="btn btn-ghost" onClick={onCancelClick}>Cancel</button>
        <button className="btn btn-primary" onClick={onDoneClick}>Done</button>
      </div>
    </div>
  );
}

/** day boundary helpers to respect min/max */
function startOfDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function endOfDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

CustomCalendar.propTypes = {
  mode: PropTypes.string,
  value: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.shape({
      start: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
      end: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.oneOf([null])]),
    }),
  ]),
  onChange: PropTypes.func,
  onDone: PropTypes.func,
  onCancel: PropTypes.func,
  minDate: PropTypes.instanceOf(Date),
  maxDate: PropTypes.instanceOf(Date),
  initialMonth: PropTypes.instanceOf(Date),
  showSlider: PropTypes.bool,
};
